'use client';

/* eslint-disable spellcheck/spell-checker */
/**
 * @see https://postcode.map.daum.net/guide
 */
import React from 'react';
import Script from 'next/script';

import { isClient } from '~/utils/ssr';
import type { Address } from './types';

declare global {
  interface Window {
    daum: Daum;
  }
}

type Daum = {
  postcode: {
    load: (fn: () => void) => void;
    version: string;
    _validParam_: boolean;
  };
  Postcode: {
    new (postcodeOptions: PostcodeOptions): Postcode;
  };
};

type EmbedOptions = {
  q?: string;
  autoClose?: boolean;
};

type Postcode = {
  embed(element: HTMLElement, embedOptions?: EmbedOptions): void;
};

type PostcodeOptions = {
  oncomplete?: (address: Address) => void;
  width?: string | number;
  height?: string | number;
};

type DaumPostCodeProps = {
  onComplete: (data: Address) => void;
};

export function DaumPostCode({ onComplete }: DaumPostCodeProps) {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const [ready, setReady] = React.useState(
    () => isClient() && Boolean(window.daum),
  );

  React.useEffect(() => {
    if (!window.daum || !ready) {
      return;
    }

    // 이미 embed가 되어있을 때 다시 embed하게되면 에러가 나기 때문에 조건을 추가
    if (!wrapperRef.current || wrapperRef.current?.hasChildNodes()) {
      return;
    }

    const Postcode = new window.daum.Postcode({
      oncomplete(data: Address) {
        onComplete(data);
      },
      width: '100%',
      height: '100%',
    });

    Postcode.embed(wrapperRef.current);
  }, [onComplete, ready]);

  return (
    <>
      <Script
        onLoad={() => setReady(true)}
        src="https://t1.kakaocdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js"
      />
      <div
        ref={wrapperRef}
        style={{ width: '100%', height: `${window.innerHeight - 52}px` }}
      />
    </>
  );
}
